import { CreateElement, VNode, VNodeData } from 'vue';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { applyClasses } from '../../lib';

interface Classes {
  input?: any;
  component?: any;
  root?: any;
  textarea?: any;
}

@Component
export default class ClInputBase extends Vue {
  @Prop({
    type: String,
    default: 'off'
  })
  public autoComplete!: 'on' | 'off';

  @Prop({
    type: Boolean,
    default: false
  })
  public autoFocus!: boolean;

  @Prop({
    type: String,
    default: 'input'
  })
  public component!: string;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public componentProps!: object;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public inputAttrs!: object;

  /**
   * The classnames of the internal components.
   */
  @Prop({
    type: Object as () => Classes,
    default: () => ({
      root: '',
      input: '',
      textarea: '',
      component: ''
    })
  })
  public classes!: Classes;

  @Prop({
    type: Boolean,
    default: false
  })
  public disabled!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public error!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public fullWidth!: boolean;

  @Prop(String)
  public id!: string;

  @Prop(Number)
  public max!: number;

  @Prop(Number)
  public min!: number;

  @Prop({
    type: Boolean,
    default: false
  })
  public multiline!: boolean;

  @Prop(String)
  public name!: string;

  @Prop(String)
  public placeholder!: string;

  @Prop({
    type: Boolean,
    default: false
  })
  public readOnly!: boolean;

  @Prop({
    type: Boolean,
    default: false
  })
  public required!: boolean;

  /**
   * Number of rows to display when multiline option is set to true.
   */
  @Prop({
    type: [Number, String]
  })
  public rows!: number | string;

  /**
   * Maximum number of rows to display when multiline option is set to true.
   */
  @Prop({
    type: [Number, String]
  })
  public rowsMax!: number | string;

  @Prop(Number)
  public step!: number;

  @Prop(String)
  public type!: string;

  /**
   * The value of the component.
   */
  @Prop({
    type: [String, Number]
  })
  public value!: string | number;

  public render(h: CreateElement): VNode {
    const Component = this.multiline ? 'textarea' : this.component;
    return (
      <div
        class={applyClasses(
          {
            'cl-input-base': true,
            'cl-input-base--disabled': this.disabled,
            'cl-input-base--full-width': this.fullWidth
          },
          this.classes.root
        )}
      >
        {this.$slots['start-adornment']}
        <Component
          {...{
            propsData: this.componentProps,
            domProps: {
              value: this.value
            },
            attrs: {
              ...this.inputAttrs,
              autocomplete: this.autoComplete,
              autofocus: this.autoFocus,
              disabled: this.disabled,
              id: this.id,
              max: this.max,
              min: this.min,
              placeholder: this.placeholder,
              rows: this.rows,
              readonly: this.readOnly,
              required: this.required,
              step: this.step,
              type: this.type
            },
            on: {
              blur: (event: FocusEvent): void => {
                this.$emit('blur', event);
              },
              change: (event: Event): void => {
                this.$emit('change', event);
              },
              focus: (event: FocusEvent): void => {
                this.$emit('focus', event);
              },
              input: (event: Event): void => {
                const { value } = event.target as HTMLInputElement;
                this.$emit('input', value);
              },
              keyup: (event: KeyboardEvent): void => {
                this.$emit('keyup', event);
              },
              keydown: (event: KeyboardEvent): void => {
                this.$emit('keydown', event);
              }
            },
            class: applyClasses(
              {
                'cl-input-base__component': true,
                'cl-input-base__component--error': this.error,
                'cl-input-base__component--with-start-adornment': Boolean(
                  this.$slots['start-adornment']
                ),
                'cl-input-base__component--with-end-adornment': Boolean(
                  this.$slots['end-adornment']
                ),
                'cl-input-base--mutliline--no-resize': Boolean(this.rowsMax)
              },
              this.classes.component,
              this.classes.input,
              this.classes.textarea,
              (this.componentProps as any).class
            )
          }}
        />
        {this.$slots['end-adornment']}
      </div>
    );
  }
}
